import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { getColorWithAlpha, isDarkColor } from '@elfsight/lib-utils';
import { focusVisibleMixin, screenReaderOnlyMixin } from '@elfsight/widget-ui';
import { getHandleActionKeyDown } from '@elfsight/widget-utils';
import { ANIMATION_SPEED_MS } from '../../constants';
import type { PollSettings, Vote } from '../../types';
import { useAnimationContext } from '../providers';
import { formatNumberByLanguage } from '../utils';
import { AnimateChildren, AnimateDataAttribute } from './animate-counter';

const ANSWER_VOTES_TEXT_STYLE = {
  lineHeight: '1.28571',
  fontSize: '14px',
  fontWeight: '400'
};

const RIGHT_SLOT_TEXT_STYLE = {
  lineHeight: '1.375',
  fontSize: '16px',
  fontWeight: '700'
};

type AnswerButtonProps = {
  id: string;
  showVotes: boolean;
  active: boolean;
  answer: string;
  accentColor: PollSettings['accentColor'];
  textColor: PollSettings['textColor'];
  language: string | undefined;
  resultView: PollSettings['resultView'];
  data: Vote;
  onClick(): void;
};

export function AnswerButton({
  id,
  showVotes,
  active,
  answer,
  accentColor,
  textColor,
  language,
  resultView,
  data,
  onClick
}: AnswerButtonProps) {
  const { buttonAnimationEnd } = useAnimationContext();

  const [answerAnimate, setAnswerAnimate] = useState(false);
  const [percentAnimate, setPercentAnimate] = useState(false);
  const [afterWidth, setAfterWidth] = useState(0);
  const [rightSlotWidth, setRightSlotWidth] = useState(0);

  const { count, percentage } = data;
  const answerHiddenId = showVotes ? `es-answer-hidden-${id}` : undefined;

  const votesOnly = resultView === 'votes';
  const votesAndPercentages = resultView === 'votesAndPercentages';
  const votesCount = formatNumberByLanguage(count, language);
  const showVotesCount = showVotes && votesAndPercentages;

  const answerAriaDescriptionMap = {
    votes: `${votesCount} votes`,
    percentages: `${percentage}%`,
    votesAndPercentages: `${votesCount} votes, ${percentage}%`
  };

  const lightThemeStyle = {
    _textColor: textColor,
    _rightSlotColor: textColor,
    _votesColor: getColorWithAlpha(textColor, 0.5),
    _backgroundColor: getColorWithAlpha(accentColor, 0.1),
    _backgroundColorOnHover: getColorWithAlpha(accentColor, 0.15),
    _barColor: getColorWithAlpha(accentColor, 0.15),
    _borderColor: accentColor
  };

  const darkThemeStyle = {
    _textColor: textColor,
    _rightSlotColor: textColor,
    _votesColor: getColorWithAlpha(textColor, 0.7),
    _backgroundColor: getColorWithAlpha(accentColor, 0.2),
    _backgroundColorOnHover: getColorWithAlpha(accentColor, 0.25),
    _barColor: getColorWithAlpha(accentColor, 0.25),
    _borderColor: accentColor
  };

  const isTextColorDark = isDarkColor(textColor);
  const currentThemeStyle = isTextColorDark ? lightThemeStyle : darkThemeStyle;

  useEffect(() => {
    if (votesAndPercentages) {
      if (answerAnimate && percentAnimate) {
        setAnswerAnimate(false);
        setPercentAnimate(false);
        buttonAnimationEnd(id);
      }

      return;
    }

    if (percentAnimate) {
      setPercentAnimate(false);
      buttonAnimationEnd(id);
    }
  }, [votesAndPercentages, answerAnimate, percentAnimate]);

  return (
    <Container
      {...currentThemeStyle}
      _focusColor={accentColor}
      _percentage={percentage}
      _showVotes={showVotes}
      _showSelectedAnswer={showVotes && active}
      role="button"
      aria-label={answer}
      aria-pressed={active}
      aria-describedby={answerHiddenId}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={getHandleActionKeyDown(onClick)}
    >
      <Bar
        {...currentThemeStyle}
        style={{
          borderRadius: `8px ${percentage === 100 ? '8px 8px' : '0px 0px'} 8px`,
          width: `${showVotes ? percentage : 0}%`
        }}
      />

      <Answer
        {...currentThemeStyle}
        _showVotes={showVotesCount}
        _afterWidth={afterWidth}
        _answerVotesTextStyle={ANSWER_VOTES_TEXT_STYLE}
        animateSlotStyle={ANSWER_VOTES_TEXT_STYLE}
        language={language}
        showVotes={showVotes}
        data-count={showVotesCount ? votesCount : '0'}
        onAnimationEnd={() => setAnswerAnimate(true)}
        onAnimateSlotWidthChange={setAfterWidth}
      >
        {answer}
      </Answer>

      {showVotes && (
        <>
          <RightSlot
            _showPercentage={!votesOnly}
            _width={rightSlotWidth}
            animateSlotStyle={RIGHT_SLOT_TEXT_STYLE}
            language={language}
            showVotes={showVotes}
            onAnimationEnd={() => setPercentAnimate(true)}
            onAnimateSlotWidthChange={setRightSlotWidth}
          >
            {votesOnly ? votesCount : percentage}
          </RightSlot>

          <AnswerHidden id={answerHiddenId}>
            {answerAriaDescriptionMap[resultView]}
          </AnswerHidden>
        </>
      )}
    </Container>
  );
}

const Container = styled.div<{
  _percentage: number;
  _showVotes: boolean;
  _showSelectedAnswer: boolean;
  _textColor: string;
  _backgroundColor: string;
  _backgroundColorOnHover: string;
  _barColor: string;
  _borderColor: string;
  _focusColor: string;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  min-height: 50px;
  padding: 14px 24px;
  border-radius: 8px;
  color: ${({ _textColor }) => _textColor};
  background-color: ${({ _backgroundColor }) => _backgroundColor};
  transition: background-color 0.2s ease;
  ${({ _showSelectedAnswer, _borderColor }) =>
    _showSelectedAnswer && `box-shadow: 0 0 0 2px ${_borderColor}`};

  &:hover,
  &:focus-visible {
    background-color: ${({ _backgroundColorOnHover }) =>
      _backgroundColorOnHover};
  }

  ${({ _focusColor }) => focusVisibleMixin(_focusColor)}
`;

const Bar = styled.div<{
  _barColor: string;
}>`
  position: absolute;
  inset: 0;
  will-change: width;
  transition: width ${ANIMATION_SPEED_MS}ms ease;
  background-color: ${({ _barColor }) => _barColor};
`;

const Answer = styled(AnimateDataAttribute)<{
  _votesColor: string;
  _showVotes: boolean;
  _answerVotesTextStyle: CSSProperties;
  _afterWidth: number;
}>`
  position: relative;
  font-size: 16px;
  line-height: 1.375;
  font-weight: 500;
  flex-grow: 1;

  ${({ _votesColor, _showVotes, _answerVotesTextStyle, _afterWidth }) =>
    _showVotes &&
    css`
      &:after {
        content: attr(data-count);
        display: inline-block;
        font-size: ${_answerVotesTextStyle.fontSize};
        line-height: ${_answerVotesTextStyle.lineHeight};
        font-weight: ${_answerVotesTextStyle.fontWeight};
        min-width: ${_afterWidth}px;
        margin-left: 8px;
        vertical-align: text-bottom;
        color: ${_votesColor};
      }
    `}
`;

const RightSlot = styled(AnimateChildren)<{
  _showPercentage: boolean;
  _width: number;
}>`
  position: relative;
  font-size: 16px;
  line-height: 1.375;
  font-weight: 700;
  white-space: nowrap;
  min-width: ${({ _width }) => _width + 17}px; /* 17px is '%' width */

  ${({ _showPercentage }) =>
    _showPercentage &&
    css`
      &:after {
        content: '%';
      }
    `}
`;

const AnswerHidden = styled.div`
  ${screenReaderOnlyMixin}
`;
